/**
 * **The token command menu** — `projects/token-command-menu.md` 2.1/2.2, the floating card stack
 * docked at the top of the unit column, the combat canvas's top-left, with the unit panel at the
 * bottom-left (`UnitColumn.tsx`, `projects/unit-panel.md`); it grows downward and its stack's height slides
 * between screens (`useAnimatedHeight`) (v18 of the prototype's `project/Main.dc.html` and
 * `project/ActionMenu.dc.html`, with v17's card tilt).
 *
 * A fixed header (the two diamonds, whose turn it is) sits above a stack of
 * cards, one per screen of {@link commandMenuTree}. Every card is always mounted and its role sets
 * its transform (`token-command-menu.css` carries the timings): the `active` screen; the `preview`
 * that floats in to the right while a group row is hovered or focused; `left` for the active
 * screen's ancestors; `right` for everything else. Clicking a group row, or the preview card, makes
 * that screen active; Back reverses it, and so does Esc (a layer-4 entry, `decisions/0189`).
 *
 * While an aim is armed (targeting mode, 2.5/2c.9) the host passes `aiming`: the menu holds its card
 * until the shot resolves. It never slides away: it stays docked and interactive, so choosing
 * another row re-aims.
 *
 * **The fire card** (Phase 3, R7; the prototype's `bfire`) is the `fire` screen, present while a
 * Skirmish or Barrage is committed or its lock is open. It lists the committed mounts' weapons
 * ({@link FireList}) with the armed (else the first fireable) one selected; clicking a weapon arms
 * it (targeting mode). There is no FIRE button ("Barrage fires from targeting mode", 2026-09-24): a
 * map click fires the armed weapon. The card is an ordinary screen of the stack (it slides in like
 * any other, `barrage-card-submenu`); while a shot is armed it carries that shot's controls (the
 * host's `panels.fire`) and a FIRED / LEFT progress line. The bottom button is CANCEL, STOP FIRING (a Barrage on one mount converts to Skirmish), or
 * END BARRAGE EARLY. The card appears when the commitment does (the Barrage confirm, a Skirmish
 * mount) and, once its lock closes, the menu lands on the card holding the struck row
 * ({@link homeOf}); a CANCEL before the first shot lands back on the mount card. Back is locked
 * after the first shot. Everything it shows past the first shot is the server-synced lock's.
 *
 * **Live locks (3.3).** A menu mounted while the unit's attack is open (reopened, a reload, a
 * second client) opens on its live card ({@link liveScreenOf}): the fire card for a Skirmish or
 * Barrage, the live Full Tech card (its options as direct picks, the lock's progress, END FULL
 * TECH, Back locked) for a Full Tech. Both come and go with the server's lock, never local state.
 *
 * **Sections (`decisions/0186` item 2).** A group card lists its rows by {@link CommandMenuRow.section}:
 * a quiet "Start of turn" heading over its Protocols, a "Free" heading over its Free rows, then the
 * Quick and Full rows with no heading ({@link sectionedRows}). Once the unit has moved or acted
 * ({@link CommandMenuTree.protocolWindowClosed}) the Start of turn section is one muted line, "Start
 * of turn: back next turn". A Skirmish or Barrage mount row says how many hostiles it reaches.
 *
 * **Say it once (`projects/menu-advisory-diet.md`).** A reason that holds for the whole unit is the
 * tree's `unitLine`, one strip under the header; a row the wall greys shows no line or tooltip of its
 * own ({@link rowSub}, {@link rowTitle}). A unit that isn't acting gets its reaction line under it
 * (F2). A DONE unit's menu is compact, with no cards at all; a Stunned, Shut Down or holding unit's
 * is the root card alone, holding what it can still do (F1, F3). END TURN sits under the whole
 * stack, so it is reachable from any screen, while the unit is acting or READY to act
 * (`turn-presence` Decision 2 A), and confirms first while actions are left (`useConfirmEndTurn`).
 *
 * This file is the view and nothing else. It never calls a handler: a chosen row goes to `onRow`,
 * a hovered or focused leaf row to `onPreview` (the host turns it into the board's hover wash),
 * a pick screen's confirm to `onConfirm`, the fire card's weapon picks and bottom button to
 * `onFire` and `onFireExit`, END TURN to `onEndTurn`, and the host (`TokenCommandMenuHost.tsx`)
 * maps each onto the Phase 1 model through `commandMenuActions.ts`.
 */
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import {
  COMMAND_MENU_SECTION_HEADING,
  COMMAND_MENU_SECTION_TERM,
  COMMAND_MENU_HEAD_TERM,
  FULL_TECH_END_LABEL,
  NOTHING_TO_USE,
  START_OF_TURN_CLOSED_NOTE,
  fireProgress,
  fullTechProgress,
  homeOf,
  openerOf,
  inReachLine,
  liveScreenOf,
  pickReady,
  reactionLine,
  togglePick,
  usedKeyOf,
  type CommandMenuFire,
  type CommandMenuPick,
  type CommandMenuRow,
  type CommandMenuScreen,
  type CommandMenuScreenId,
  type CommandMenuSection,
  type CommandMenuTree,
  type FireAction,
  type FireExit,
} from "./commandMenu";
import { Term } from "../glossary/Term";
import { GLOSSARY_LINES, glossaryEntry, type ActionTypeId } from "../glossary/combatGlossary";
import { CompendiumContext } from "../CompendiumProvider";
import { EscLayersContext, useEscEntry } from "./escLayers";
import { useConfirmEndTurn } from "./useConfirmEndTurn";
import { useAnimatedHeight } from "./useAnimatedHeight";
import { rulesPlainText } from "../RulesText";
import "./token-command-menu.css";

/** A card's place in the stack. */
export type TcmCardRole = "active" | "preview" | "left" | "right";

/**
 * Screen render order: root, the five groups, then the drill-downs in their groups' order. A screen
 * missing here never renders, so the check below fails typecheck on one.
 */
const SCREEN_ORDER = ["root", "attack", "skirmish", "barrage", "fire", "jockey", "reload", "hack", "quick_tech", "full_tech", "maneuver", "boost", "search", "disarm_mine", "prepare", "equipment", "system", "stabilize", "self_destruct", "mount", "ride"] as const satisfies readonly CommandMenuScreenId[];
const _everyScreenOrdered: [Exclude<CommandMenuScreenId, (typeof SCREEN_ORDER)[number]>] extends [never] ? true : never = true;

/** The hosted chooser card the tree holds, if any (it holds at most one: one aim board-wide). */
function chooserScreenOf(tree: CommandMenuTree): CommandMenuScreenId | null {
  return Object.values(tree.screens).find((s) => s?.chooser)?.id ?? null;
}

/** The ancestors of `id`, nearest first. */
export function screenAncestors(tree: CommandMenuTree, id: CommandMenuScreenId): CommandMenuScreenId[] {
  const out: CommandMenuScreenId[] = [];
  let cur = tree.screens[id]?.parent;
  while (cur) {
    out.push(cur);
    cur = tree.screens[cur]?.parent;
  }
  return out;
}

/** The role `screen` plays given the active and previewed screens (the prototype's `cards` map). */
export function cardRole(args: { tree: CommandMenuTree; screen: CommandMenuScreenId; active: CommandMenuScreenId; preview: CommandMenuScreenId | null }): TcmCardRole {
  const { tree, screen, active, preview } = args;
  if (screen === active) return "active";
  if (screen === preview) return "preview";
  return screenAncestors(tree, active).includes(screen) ? "left" : "right";
}

/** Diamonds the hovered row would spend — what the header pulses. */
function pendingDiamonds(args: { screen: CommandMenuScreen; hover: string | null; picks: string[] }): number {
  const { screen, hover, picks } = args;
  if (hover === CONFIRM_ID && screen.pick) return pickReady(screen, picks) ? screen.pick.diamonds : 0;
  const row = screen.rows.find((r) => r.id === hover);
  // A weapon before the first shot spends the action: both diamonds for a Barrage, one for a Skirmish.
  if (screen.fire && row?.kind === "weapon") return row.available && screen.fire.fired.length === 0 ? (screen.fire.action === "barrage" ? 2 : 1) : 0;
  return row && row.available ? row.diamonds : 0;
}

const CONFIRM_ID = "__confirm";

/** The fire card's selected weapon: the armed one, else the first fireable. */
export function fireSelection(fire: CommandMenuFire, armed: string | undefined): string | null {
  if (armed !== undefined && fire.fireable.includes(armed)) return armed;
  return fire.fireable[0] ?? null;
}

/** What the host did with a chosen row (`commandMenuRowEffect`'s kind). */
export type MenuRowOutcome = "fire" | "target" | "chooser" | "none";

export type TokenCommandMenuProps = {
  tree: CommandMenuTree;
  unitName: string;
  /** The header's turn line (`commandMenuStateFor`): `YOUR TURN`, `READY`, `WAITING` or `DONE`. */
  headline: string;
  /** Targeting mode is on (an aim is armed): a targeted row's card waits for its shot to resolve. */
  aiming: boolean;
  /**
   * A leaf row was chosen; the host runs it and says what it did. A `fire` row lands the menu on
   * the card holding the row it marks used ({@link homeOf}, the v19 ruling); a `target` row keeps
   * the card until the shot resolves, then lands the same way; a `chooser` row's card appears in the
   * tree once its aim is armed, and the menu goes to it.
   */
  onRow: (row: CommandMenuRow, screen: CommandMenuScreenId) => MenuRowOutcome;
  /**
   * A pick screen's confirm row was chosen with its picks; the host says what it did. A `target`
   * confirm (Full Tech's "Run both") keeps the card until the picks resolve, then lands like a
   * targeted row.
   */
  onConfirm: (screen: CommandMenuScreenId, picks: string[]) => MenuRowOutcome;
  /** Back on a hosted chooser's card: the host disarms the chooser, and the card goes. */
  onChooserCancel?: () => void;
  /** A weapon picked on the fire card: arm this weapon's shot (targeting mode). */
  onFire?: (weaponKey: string, attack: FireAction) => void;
  /** The fire card's bottom button (and Back before the first shot, which cancels). */
  onFireExit?: (exit: FireExit) => void;
  /** The weapon of the armed aim, when it is the fire card's attack. */
  armedWeapon?: string;
  /** The live Full Tech card's END FULL TECH (3.3): the host ends the open lock. */
  onFullTechEnd?: () => void;
  /**
   * Controls the host renders on a card while it is active, by screen: Invade's stepper on the
   * Quick Tech and Full Tech cards, and a hosted chooser's own controls on its card (Stabilize,
   * Search, Self-Destruct, Prepare), and the armed shot's controls on the fire card.
   */
  panels?: Partial<Record<CommandMenuScreenId, ReactNode>>;
  onEndTurn: () => void;
  /**
   * A leaf row is hovered or focused (`null` when it is left): the host paints its hover preview.
   * `weaponKey` names the weapon under the pointer inside a mount row, which previews that weapon.
   */
  onPreview: (row: CommandMenuRow | null, screen: CommandMenuScreenId, weaponKey?: string) => void;
  /** The active screen, on each change, and `null` when the menu unmounts. */
  onActiveScreen?: (screen: CommandMenuScreenId | null) => void;
  /**
   * While the unit panel is expanded into the sheet (F1: A), the menu folds to a one-line strip,
   * `<UNIT> · ACTIONS ◆◆ ▾`, and a click on it calls this (the host collapses the sheet). The menu
   * stays mounted, so it comes back on the screen it was on.
   */
  onStrip?: () => void;
};

export function TokenCommandMenu({ tree, unitName, headline, aiming, onRow, onConfirm, onChooserCancel, onFire, onFireExit, armedWeapon, onFullTechEnd, panels, onEndTurn, onPreview, onActiveScreen, onStrip }: TokenCommandMenuProps) {
  // Reopened mid-attack (a lock is open), the menu opens on its live card; with a chooser armed, on its card.
  const [screenId, setScreenId] = useState<CommandMenuScreenId>(() => liveScreenOf(tree) ?? chooserScreenOf(tree) ?? "root");
  const [preview, setPreview] = useState<CommandMenuScreenId | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [picks, setPicks] = useState<Partial<Record<CommandMenuScreenId, string[]>>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  // The stack's height slides as screens change, so the menu's bottom edge moves smoothly.
  const stackRef = useRef<HTMLDivElement>(null);
  useAnimatedHeight(stackRef);
  // A targeted row armed from this menu, waiting for its shot to resolve: the key it marks used and
  // the diamonds left when it armed. Either one changing is the resolution; the menu then lands.
  const [awaiting, setAwaiting] = useState<{ key: string; diamonds: number } | null>(null);
  // While a press that began outside the menu is held (a token drag, a pan), the menu lets the
  // pointer through, so the board keeps receiving the gesture's moves and its release even when
  // the pointer crosses the menu.
  const [passthrough, setPassthrough] = useState(false);
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setPassthrough(true);
    };
    const up = () => setPassthrough(false);
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", up, true);
    };
  }, []);

  // A screen that left the tree (a chooser whose aim cleared) shows the card holding its row until
  // the effects below move there for good.
  // A compact menu is its root card alone (F1, F3).
  const active = tree.compact ? "root" : tree.screens[screenId] ? screenId : homeOf(tree, screenId);
  const activeScreen = tree.screens[active] ?? tree.screens.root;
  const activePicks = picks[active] ?? [];

  // Keyboard users follow the active card: when the screen changes with focus inside the menu,
  // focus moves to the new card's first row.
  const lastActive = useRef(active);
  useLayoutEffect(() => {
    if (lastActive.current === active) return;
    lastActive.current = active;
    const root = rootRef.current;
    if (!root || !root.contains(document.activeElement)) return;
    root.querySelector<HTMLElement>(".tcm-card.is-active .tcm-row")?.focus();
  }, [active]);

  const onActiveScreenRef = useRef(onActiveScreen);
  onActiveScreenRef.current = onActiveScreen;
  useEffect(() => onActiveScreenRef.current?.(active), [active, onActiveScreen]);
  useEffect(() => () => onActiveScreenRef.current?.(null), []);

  const go = (id: CommandMenuScreenId) => {
    setScreenId(id);
    setPreview(null);
    setHover(null);
    setAwaiting(null);
  };
  // The armed shot resolved (its row is now used, or a diamond was spent) and targeting mode is
  // over (a Full Tech's second pick is still armed after the first spends): land on its card.
  useEffect(() => {
    if (!awaiting || aiming) return;
    // A Skirmish or Full Tech whose lock opened stays on its live card, which lands once the lock closes.
    if (tree.screens.fire?.fire?.live || tree.screens.full_tech?.fullTech) return setAwaiting(null);
    const home = homeOf(tree, awaiting.key);
    const used = tree.screens[home]?.rows.some((r) => r.id === awaiting.key && r.usedThisTurn) ?? false;
    if (used || tree.diamondsLeft < awaiting.diamonds) go(home);
  }, [tree, awaiting, aiming]);
  // The fire card comes and goes with the commitment: it appears when a Skirmish mount or the
  // Barrage confirm commits, and once its lock closes (every weapon fired, Stop Firing, End Early)
  // the menu lands on the card holding the struck row. A commitment dropped with nothing fired and
  // the action unused (the card's CANCEL) lands on its mount card. The struck row is the one that
  // opens the mount card (`openerOf`): a pilot's Fight opens the `skirmish` card. A pilot's Sidearm
  // Fight can stay unstruck after its shot, so a spent diamond also counts as the action taken.
  const fire = tree.screens.fire?.fire;
  const hadFire = useRef<{ action: FireAction; fired: number; diamonds: number } | null>(fire ? { action: fire.action, fired: fire.fired.length, diamonds: tree.diamondsLeft } : null);
  useEffect(() => {
    const had = hadFire.current;
    hadFire.current = fire ? { action: fire.action, fired: fire.fired.length, diamonds: tree.diamondsLeft } : null;
    if (fire && had === null) {
      go("fire");
    } else if (!fire && had !== null && screenId === "fire") {
      const was = had.action;
      const opener = openerOf(tree, was);
      const home = opener?.screen ?? homeOf(tree, was);
      const used = had.fired > 0 || opener?.row.usedThisTurn === true || tree.diamondsLeft < had.diamonds;
      go(used || !tree.screens[was] ? home : was);
    }
  }, [fire, tree, screenId]);
  // The live Full Tech card (3.3) comes with its lock (the first pick lands, or another client's
  // does) and, once the lock closes, the menu lands on Hack with Full Tech struck.
  const fullTechLive = tree.screens.full_tech?.fullTech !== undefined;
  const hadFullTech = useRef(fullTechLive);
  useEffect(() => {
    const was = hadFullTech.current;
    hadFullTech.current = fullTechLive;
    if (fullTechLive && !was) go("full_tech");
    else if (!fullTechLive && was && screenId === "full_tech") go(homeOf(tree, "full_tech"));
  }, [fullTechLive, tree, screenId]);
  // A hosted chooser's card comes and goes with its armed aim (3.2): the menu goes to it when it is
  // armed, and when it clears (fired, Back, Esc) lands on the card holding its row, where the row
  // is struck once the action resolves.
  const chooser = chooserScreenOf(tree);
  const hadChooser = useRef<CommandMenuScreenId | null>(chooser);
  useEffect(() => {
    const was = hadChooser.current;
    hadChooser.current = chooser;
    if (chooser && chooser !== was) go(chooser);
    else if (!chooser && was !== null && screenId === was) go(homeOf(tree, was));
  }, [chooser, tree, screenId]);
  // A form card (Boost's opt-in riders) lands on the card holding its row once, while it is open,
  // its action is taken: that row turns used. A form opened on an already-used row stays (always
  // explorable), so only the change while on the card counts.
  const formParent = activeScreen.form ? activeScreen.parent : undefined;
  const formRowUsed = formParent ? (tree.screens[formParent]?.rows.some((r) => r.child === active && r.usedThisTurn) ?? false) : false;
  const formSeen = useRef<{ screen: CommandMenuScreenId; used: boolean }>({ screen: active, used: formRowUsed });
  useEffect(() => {
    const seen = formSeen.current;
    formSeen.current = { screen: active, used: formRowUsed };
    if (formParent && seen.screen === active && !seen.used && formRowUsed) go(formParent);
  }, [active, formParent, formRowUsed]);
  const back = () => {
    if (activeScreen.chooser) onChooserCancel?.();
    if (activeScreen.fullTech) return;
    if (active === "fire") {
      if (fire?.backLocked) return;
      onFireExit?.("cancel");
    }
    if (activeScreen.parent) go(activeScreen.parent);
  };
  // Esc steps back one screen before the menu hides (`decisions/0189` point 3): the menu holds a
  // standing layer-4 entry, live on every screen but the root, that runs the same `back()` as BACK.
  // Standing, because one step back usually lands on another non-root screen. A back-locked live
  // card (a fire sequence after its first shot, Full Tech) counts as the root, so Esc falls through.
  const backStops = active === "root" || activeScreen.fullTech !== undefined || (active === "fire" && fire?.backLocked === true);
  const escId = `menu-screen:${useId()}`;
  useEscEntry(useContext(EscLayersContext), { layer: "menuScreen", id: escId, active: true, live: () => !backStops, onDismiss: back });
  const fireSel = fire ? fireSelection(fire, armedWeapon) : null;
  // A weapon pick arms its shot: targeting mode, whose panel lists the same weapons.
  const chooseWeapon = (row: CommandMenuRow) => {
    if (row.available && row.attack) onFire?.(row.id, row.attack);
  };
  const exitFire = () => {
    if (!fire) return;
    onFireExit?.(fire.exit);
    if (fire.exit === "cancel" && activeScreen.parent) go(activeScreen.parent);
  };
  const choose = (row: CommandMenuRow) => {
    // Always explorable (2c): a drilling row opens its screen whatever its availability.
    if (row.child) return go(row.child);
    if (row.kind === "weapon") return chooseWeapon(row);
    if (!row.available) return;
    if (activeScreen.pick) {
      setPicks({ ...picks, [active]: togglePick(activeScreen, picks[active] ?? [], row) });
      return;
    }
    const kind = onRow(row, active);
    const key = usedKeyOf(row, active);
    if (kind === "fire") {
      setPicks({});
      go(homeOf(tree, key));
    } else if (kind === "target") {
      setAwaiting({ key, diamonds: tree.diamondsLeft });
    }
  };
  const confirm = () => {
    if (!activeScreen.pick || !pickReady(activeScreen, activePicks)) return;
    const kind = onConfirm(active, activePicks);
    setPicks({});
    if (kind === "target") setAwaiting({ key: active, diamonds: tree.diamondsLeft });
  };
  const enterRow = (row: CommandMenuRow, weaponKey?: string) => {
    setHover(row.id);
    setPreview(row.child ?? null);
    // The host decides whether a leaf row previews (`commandMenuPreviewAim`).
    onPreview(row.child ? null : row, active, weaponKey);
  };
  const leaveRow = () => {
    setHover(null);
    onPreview(null, active);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const rows = Array.from(rootRef.current?.querySelectorAll<HTMLElement>(".tcm-card.is-active .tcm-row") ?? []);
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => rows[(i + rows.length) % rows.length]?.focus();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusAt(at < 0 ? 0 : at + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusAt(at < 0 ? rows.length - 1 : at - 1);
        break;
      case "ArrowRight": {
        const row = activeScreen.rows.find((r) => r.id === (document.activeElement as HTMLElement | null)?.dataset.rowId);
        if (row?.child) {
          e.preventDefault();
          go(row.child);
        }
        break;
      }
      case "ArrowLeft":
      case "Backspace":
        if (!activeScreen.parent) break;
        e.preventDefault();
        // The stage's own window listener deletes a selected annotation on Backspace.
        e.nativeEvent.stopPropagation();
        back();
        break;
    }
  };

  const remaining = Math.max(0, Math.min(2, tree.diamondsLeft));
  const pend = Math.min(remaining, pendingDiamonds({ screen: activeScreen, hover, picks: activePicks }));
  const screens = useMemo(() => (tree.compact === "done" ? [] : tree.compact === "wall" ? ["root" as const] : SCREEN_ORDER.filter((id) => tree.screens[id] !== undefined)), [tree]);

  if (onStrip) {
    return (
      <div ref={rootRef} className="tcm is-strip" role="dialog" aria-label={`Command menu · ${unitName}`}>
        <button type="button" className="tcm-strip" aria-expanded={false} aria-label={`Show ${unitName}'s actions`} onClick={onStrip}>
          <span className="tcm-strip-unit">{unitName.toUpperCase()}</span>
          <span className="tcm-strip-sep">·</span>
          <span>ACTIONS</span>
          {tree.counters && (
            <span className="tcm-strip-dots" aria-label={`${remaining} of 2 actions remaining`}>
              {[0, 1].map((i) => (
                <span key={i} className={`tcm-dot${i < remaining ? " is-full" : ""}`} />
              ))}
            </span>
          )}
          <span className="tcm-strip-chev" aria-hidden>
            ▾
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`tcm${passthrough ? " is-passthrough" : ""}`}
      role="dialog"
      aria-label={`Command menu · ${unitName}`}
      onKeyDown={onKeyDown}
      onMouseLeave={() => {
        setPreview(null);
        onPreview(null, active);
      }}
    >
      <div className="tcm-head">
        {/* The counters only while the turn is open or the first action opens it (R7). */}
        <div className="tcm-dots">
          {tree.counters && (
            <>
              <Term k={COMMAND_MENU_HEAD_TERM.actions} className="tcm-dots-term" label={`${remaining} of 2 actions remaining`}>
                {[0, 1].map((i) => {
                  const full = i < remaining;
                  const pending = full && i >= remaining - pend;
                  return (
                    <span key={i} className="tcm-dot-slot">
                      <span className={`tcm-dot${full ? " is-full" : ""}${pending ? " is-pending" : ""}`} />
                    </span>
                  );
                })}
              </Term>
              {tree.moveLeft !== undefined && (
                <Term k={COMMAND_MENU_HEAD_TERM.move} className="tcm-move">
                  {tree.moveLeft} MOVE
                </Term>
              )}
              {tree.boostMove !== undefined && <span className="tcm-boost">(+{tree.boostMove} ◆)</span>}
            </>
          )}
        </div>
        <div className="tcm-turn">
          <span className="tcm-turn-line">{headline}</span>
          <span className="tcm-unit">{unitName.toUpperCase()}</span>
        </div>
      </div>
      {tree.unitLine && (
        <div className="tcm-notice" role="status">
          {tree.unitLine}
        </div>
      )}
      {tree.reaction && (
        <div className={`tcm-reaction${tree.reaction.open ? " is-open" : ""}`} data-testid="tcm-reaction">
          {reactionLine(tree.reaction)}
        </div>
      )}
      <div className="tcm-stack" ref={stackRef}>
        {screens.map((id) => {
          const role = cardRole({ tree, screen: id, active, preview });
          const screen = tree.screens[id];
          if (!screen) return null;
          return (
            <Card
              key={id}
              tree={tree}
              screen={screen}
              role={role}
              hover={role === "active" ? hover : null}
              picks={picks[id] ?? []}
              onOpen={() => go(id)}
              onBack={back}
              onChoose={choose}
              onConfirm={confirm}
              onEnter={enterRow}
              onLeave={leaveRow}
              onConfirmHover={(on) => setHover(on ? CONFIRM_ID : null)}
              fireSel={fireSel}
              fireArmed={fireSel !== null && fireSel === armedWeapon}
              panel={panels?.[id]}
              onFireExit={exitFire}
              onFullTechEnd={() => onFullTechEnd?.()}
            />
          );
        })}
      </div>
      {tree.endTurn && (
        <div className="tcm-foot">
          <EndTurnButton tree={tree} onEndTurn={onEndTurn} />
        </div>
      )}
    </div>
  );
}

/** END TURN under the card stack, confirming first while actions are left (`useConfirmEndTurn`). */
function EndTurnButton({ tree, onEndTurn }: { tree: CommandMenuTree; onEndTurn: () => void }) {
  const confirm = useConfirmEndTurn({ actionsLeft: tree.actionsLeft, ap: tree.diamondsLeft, onEnd: onEndTurn });
  return (
    <button
      type="button"
      className={`tcm-end${tree.diamondsLeft === 0 ? " is-spent" : ""}${confirm.armed ? " is-confirm" : ""}`}
      onClick={confirm.onClick}
      onPointerLeave={confirm.onPointerLeave}
    >
      {confirm.label}
    </button>
  );
}

function Card(props: {
  tree: CommandMenuTree;
  screen: CommandMenuScreen;
  role: TcmCardRole;
  hover: string | null;
  picks: string[];
  onOpen: () => void;
  onBack: () => void;
  onChoose: (row: CommandMenuRow) => void;
  onConfirm: () => void;
  onEnter: (row: CommandMenuRow, weaponKey?: string) => void;
  onLeave: () => void;
  onConfirmHover: (on: boolean) => void;
  fireSel: string | null;
  /** The selected weapon's shot is armed (targeting mode), not only the next up. */
  fireArmed: boolean;
  panel: ReactNode;
  onFireExit: () => void;
  onFullTechEnd: () => void;
}) {
  const { tree, screen, role, hover, picks } = props;
  const live = role === "active";
  const isRoot = screen.id === "root";
  const fire = screen.fire;
  const fullTech = screen.fullTech;
  const backLocked = fire?.backLocked === true || fullTech !== undefined;
  const have = Math.max(0, tree.diamondsLeft);
  const crumbs = [...screenAncestors(tree, screen.id).reverse().filter((s) => s !== "root"), screen.id].map((s) => tree.screens[s]?.title ?? "");
  return (
    <section
      className={`tcm-card is-${role}`}
      data-screen={screen.id}
      aria-hidden={live ? undefined : true}
      inert={role === "left" || role === "right" ? true : undefined}
      onClick={role === "preview" ? props.onOpen : undefined}
    >
      {!isRoot && (
        <header className="tcm-title">
          {live && (
            <button
              type="button"
              className="tcm-back"
              aria-disabled={backLocked ? true : undefined}
              title={backLocked ? (fullTech ? "Locked once the first pick has fired" : "Locked once the first shot has fired") : undefined}
              onClick={props.onBack}
            >
              ‹ BACK
            </button>
          )}
          <span className="tcm-crumbs">
            {crumbs.map((t, i) => (
              <span key={i} className={i === crumbs.length - 1 ? "tcm-crumb is-current" : "tcm-crumb"}>
                {i > 0 ? `/ ${t}` : t}
              </span>
            ))}
          </span>
        </header>
      )}
      {isRoot && tree.protocolLegendCount > 0 && (
        <div className="tcm-legend">
          <span>START OF TURN: {tree.protocolLegendCount}</span>
          <span className="tcm-legend-proto">
            <span className="tcm-legend-glyph" aria-hidden="true" />
            <Term k={COMMAND_MENU_HEAD_TERM.protocols}>{tree.protocolLegendCount === 1 ? "PROTOCOL" : "PROTOCOLS"}</Term>
          </span>
          <span>AVAILABLE</span>
        </div>
      )}
      <div className="tcm-rows" inert={live ? undefined : true}>
        {fire ? (
          <FireList screen={screen} have={have} selected={live ? props.fireSel : null} armed={props.fireArmed} hover={hover} onChoose={props.onChoose} onEnter={props.onEnter} onLeave={props.onLeave} />
        ) : (
          sectionedRows({
            rows: screen.rows,
            protocolWindowClosed: tree.protocolWindowClosed,
            renderRow: (row) => (
              <Row
                key={row.id}
                row={row}
                toggle={screen.pick !== undefined}
                picked={picks.filter((p) => p === row.id).length}
                have={have}
                hovered={hover === row.id}
                onChoose={() => props.onChoose(row)}
                onEnter={() => props.onEnter(row)}
                onWeapon={(key) => props.onEnter(row, key)}
                onLeave={props.onLeave}
              />
            ),
          })
        )}
        {isGroupScreen(screen) && screen.rows.length === 0 && <div className="tcm-empty">{NOTHING_TO_USE}</div>}
        {screen.pick && <ConfirmRow screen={screen} pick={screen.pick} picks={picks} hovered={hover === CONFIRM_ID} onConfirm={props.onConfirm} onHover={props.onConfirmHover} />}
      </div>
      {fullTech && (
        <div className="tcm-notice tcm-fulltech-progress" role="status">
          {fullTechProgress(fullTech.fired.length)}
        </div>
      )}
      {fire && (
        <div className="tcm-notice tcm-fire-progress" role="status">
          {fireProgress(fire)}
        </div>
      )}
      {live && props.panel && <div className="tcm-panel">{props.panel}</div>}
      {fullTech && (
        <div className="tcm-fire-foot">
          <FootButton label={FULL_TECH_END_LABEL} tabIndex={live ? undefined : -1} onClick={props.onFullTechEnd} />
        </div>
      )}
      {fire && (
        <div className="tcm-fire-foot">
          <FireExitButton fire={fire} tabIndex={live ? undefined : -1} onExit={props.onFireExit} />
        </div>
      )}
    </section>
  );
}

/** A group's card: the one kind of screen that can hold nothing (R5's "Nothing to use"). */
function isGroupScreen(screen: CommandMenuScreen): boolean {
  return screen.parent === "root" && !screen.chooser && !screen.form && !screen.pick && !screen.fire && !screen.fullTech;
}

/**
 * A section's heading text (`COMMAND_MENU_SECTION_HEADING`), with an optional note after it. The
 * heading is a glossary `<Term>`: Start of turn opens the Protocol rule, Free the free-action rule.
 */
function SectionLine({ section, note }: { section: Exclude<CommandMenuSection, "action">; note?: string }) {
  return (
    <div className={`tcm-section${note ? " is-closed" : ""}`} data-section={section}>
      <Term k={COMMAND_MENU_SECTION_TERM[section]} className="tcm-section-term">
        {COMMAND_MENU_SECTION_HEADING[section]}
      </Term>
      {note && `: ${note}`}
    </div>
  );
}

/**
 * A card's rows with their section headings (`decisions/0186` item 2): a heading above a non-empty
 * Start of turn or Free section, a bare rule above the Quick and Full rows that follow one. Once the unit's Protocol window
 * has closed, the Start of turn section is one muted line instead of its greyed rows. Rows without a
 * section (every screen but the five groups) render as they are.
 */
function sectionedRows(args: { rows: readonly CommandMenuRow[]; protocolWindowClosed: boolean; renderRow: (row: CommandMenuRow) => ReactNode }): ReactNode[] {
  const out: ReactNode[] = [];
  let open: CommandMenuSection | undefined;
  for (const row of args.rows) {
    const section = row.section;
    if (section === "start" && args.protocolWindowClosed) {
      if (open !== "start") out.push(<SectionLine key="section:start" section="start" note={START_OF_TURN_CLOSED_NOTE} />);
      open = "start";
      continue;
    }
    if (section !== undefined && section !== open && section !== "action") out.push(<SectionLine key={`section:${section}`} section={section} />);
    // The Quick and Full rows carry no heading, but a rule sets them off from a Free section above,
    // so a Quick row never reads as sitting under "Free".
    if (section === "action" && open !== undefined && open !== "action") out.push(<div key="section:action" className="tcm-section-break" data-section="action" aria-hidden="true" />);
    open = section;
    out.push(args.renderRow(row));
  }
  return out;
}

/**
 * The fire card's weapons (R7): the committed mounts as small headings over their weapons, the
 * `selected` one inverted, fired ones struck, blocked ones greyed with their reason.
 */
function FireList(props: {
  screen: CommandMenuScreen;
  have: number;
  selected: string | null;
  /** The selected weapon is armed: it says to click a target. Otherwise it is only the next up. */
  armed: boolean;
  hover: string | null;
  onChoose: (row: CommandMenuRow) => void;
  onEnter: (row: CommandMenuRow, weaponKey?: string) => void;
  onLeave: () => void;
}) {
  const { screen } = props;
  return (
    <>
      {(screen.fire?.groups ?? []).map((g) => (
        <div key={g.key} className="tcm-fire-group" role="group" aria-label={g.name}>
          <div className="tcm-fire-head">{g.name}</div>
          {g.weaponKeys.map((k) => {
            const row = screen.rows.find((r) => r.id === k);
            if (!row) return null;
            return (
              <Row
                key={k}
                row={row}
                toggle={false}
                picked={0}
                have={props.have}
                selected={k === props.selected}
                armed={props.armed}
                hovered={props.hover === k}
                onChoose={() => props.onChoose(row)}
                onEnter={() => props.onEnter(row)}
                onLeave={props.onLeave}
              />
            );
          })}
        </div>
      ))}
    </>
  );
}

/** The fire card's selected weapon's sub-line (the prototype's). */
export const FIRE_SELECTED_SUB = "Selected · click a target on the map";

/**
 * The line a row shows under its name, never truncated (it wraps): why the row is greyed when the
 * reason is its own, else a derived detail the row must state (Overcharge's heat cost), else a
 * fire-card weapon's selection or note. The rules blurb is not inline: it lives behind the row's (i)
 * ({@link RowInfo}), because at this width it only ever fitted cut off.
 */
export function rowSub(row: CommandMenuRow, selected = false, armed = false): { text: string; help: boolean; reach?: number } | undefined {
  // A mount with no reason of its own (it could be taken, is only unaffordable, or only the unit
  // wall greys it) says how many hostiles it reaches; a mount blocked by its weapons says why.
  if (row.kind === "mount" && row.inReach !== undefined && (row.available || row.cantAfford || row.blockedBy === "unit")) return { text: inReachLine(row.inReach), help: false, reach: row.inReach };
  // Said elsewhere: the budget by the diamonds, the unit wall by the unit line, mixed children by
  // their card, a used row by its strike (R4; a fired weapon's "Fired" is the fire card's own).
  if (row.cantAfford || row.blockedBy || (row.usedThisTurn && row.kind !== "weapon")) return undefined;
  if (!row.available) return { text: row.reasonShort ?? row.reason ?? "", help: true };
  if (row.detail) return { text: row.detail, help: false };
  if (row.kind === "weapon") {
    if (selected && armed) return { text: row.note ? `${FIRE_SELECTED_SUB} · ${row.note}` : FIRE_SELECTED_SUB, help: true };
    return row.note ? { text: row.note, help: true } : undefined;
  }
  return undefined;
}

/**
 * A row's tooltip (R6): only text its line doesn't already show, which is a mount's full weapon
 * sentence beside its short form. A reason said elsewhere (the unit line, the diamonds, the strike)
 * gets none.
 */
export function rowTitle(row: CommandMenuRow, sub: { text: string } | undefined): string | undefined {
  if (row.available || row.cantAfford || row.blockedBy || row.usedThisTurn || row.reason === undefined) return undefined;
  return sub?.text === row.reason ? undefined : row.reason;
}

/** A greyed row whose reason is said elsewhere (the diamonds, the unit line, the strike): it keeps it as its accessible label. */
function silentReason(row: CommandMenuRow): boolean {
  return !row.available && row.reason !== undefined && (row.cantAfford === true || row.blockedBy !== undefined || row.usedThisTurn);
}

/** A row's cost as its action type: the (i) popover's badge, and the popover's kind line (the type's glossary title). */
export function rowCostType(row: Pick<CommandMenuRow, "protocol" | "free" | "diamonds">): { label: string; type: ActionTypeId } | undefined {
  if (row.protocol) return { label: "Protocol", type: "protocol" };
  if (row.free) return { label: "Free", type: "free" };
  if (row.diamonds === 2) return { label: "Full", type: "full" };
  if (row.diamonds === 1) return { label: "Quick", type: "quick" };
  return undefined;
}

/**
 * A mount or fire-card weapon row's (i): every weapon tag's rule in one popover. The tags on the row
 * itself only open on mouse hover, because a click anywhere on the row arms the weapon; this is how
 * touch (and anyone who prefers a click) reads them. Nothing when the row's weapons carry no tag
 * the glossary can explain.
 */
function TagsInfo({ row, open }: { row: CommandMenuRow; open: boolean }) {
  const app = useContext(CompendiumContext);
  const lines: string[] = [];
  const seen = new Set<string>();
  for (const w of row.weapons ?? []) {
    for (const t of w.tags) {
      const key = `${t.id}:${t.val ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const e = glossaryEntry(`tag:${t.id}`, { ...(app ? { compendium: app.compendium } : {}), ...(t.val !== undefined ? { val: t.val } : {}) });
      const text = e?.text ?? t.rule;
      if (text === undefined) continue;
      lines.push(`<p><b>${e?.title ?? t.label}</b> ${text}</p>`);
    }
  }
  if (lines.length === 0) return null;
  return (
    <Term k={`action:${row.id}`} inline open={open} className="tcm-info" label={`${row.label} tags`} entry={{ title: row.label, kind: "Weapon tags", text: lines.join("") }}>
      <span className="tcm-info-glyph" aria-hidden="true">
        i
      </span>
    </Term>
  );
}

/**
 * The (i) beside a row's name: the row's rule in a glossary `<Term>`, with the row's cost as a badge
 * and its detail and blocked reason above the rule. The text is the row's own blurb (the core
 * table's, which `glossaryEntry`'s `action:` key reads too, or a grant's compendium text).
 */
function RowInfo({ row, open, shown }: { row: CommandMenuRow; open: boolean; shown: string | undefined }) {
  if (!row.blurb) return null;
  const cost = rowCostType(row);
  return (
    <Term
      k={`action:${row.actionId ?? row.id}`}
      inline
      open={open}
      className="tcm-info"
      badges={cost ? [cost.label] : []}
      note={
        <>
          {row.detail && <p className="term-pop-note">{row.detail}</p>}
          {!row.available && !row.cantAfford && !row.blockedBy && row.reason && row.reason !== shown && <p className="term-pop-note">{row.reason}</p>}
        </>
      }
      entry={{ title: row.label, kind: cost ? GLOSSARY_LINES.type[cost.type].title : "Action", text: row.blurb }}
    >
      <span className="tcm-info-glyph" aria-hidden="true">
        i
      </span>
    </Term>
  );
}

/**
 * The row's one cost slot: a fixed-width, right-aligned box holding exactly one of the Protocol
 * glyph, the FREE chip, or the diamonds, so every row's cost shares a right edge. On a row the unit
 * can't afford, the diamonds it still has (`have`) stay filled and the missing ones go hollow.
 */
function CostSlot({ row, toggle, have }: { row: CommandMenuRow; toggle: boolean; have: number }) {
  const dots = toggle || row.protocol ? 0 : row.diamonds;
  const dotClass = (i: number) => (row.cantAfford ? (i < have ? "tcm-cost-dot is-have" : "tcm-cost-dot is-short") : "tcm-cost-dot");
  return (
    <span className="tcm-cost" aria-label={dots > 0 ? `costs ${dots}` : row.protocol ? "protocol" : row.free ? "free" : undefined}>
      {row.protocol ? (
        <span className={`tcm-proto${row.available ? "" : " is-dim"}`} />
      ) : row.free && !toggle ? (
        <span className="tcm-free">FREE</span>
      ) : (
        Array.from({ length: dots }, (_, i) => <span key={i} className={dotClass(i)} />)
      )}
    </span>
  );
}

function Row({
  row,
  toggle,
  picked,
  have,
  selected = false,
  armed = false,
  hovered,
  onChoose,
  onEnter,
  onWeapon,
  onLeave,
}: {
  row: CommandMenuRow;
  toggle: boolean;
  /** How many of the screen's picks are this row (a Full Tech may pick one twice). */
  picked: number;
  /** Diamonds the unit has left: a can't-afford row's cost fills this many. */
  have: number;
  /** The fire card's selected weapon: inverted with a red bar and a left chevron. */
  selected?: boolean;
  /** The selected weapon's shot is armed. */
  armed?: boolean;
  hovered: boolean;
  onChoose: () => void;
  onEnter: () => void;
  /** A mount row's weapon gained (its key) or lost (`undefined`) the pointer: that weapon previews. */
  onWeapon?: (key: string | undefined) => void;
  onLeave: () => void;
}) {
  const sub = rowSub(row, selected, armed);
  const drills = row.child !== undefined;
  const weaponRow = row.kind === "weapon";
  const glyphInline = drills && row.protocolBelow;
  // Keyboard focus (not a mouse press) shows the row's (i) panel.
  const [keyFocus, setKeyFocus] = useState(false);
  const cls = [
    "tcm-row",
    row.kind === "mount" || weaponRow ? "is-mount" : "",
    weaponRow ? "is-weapon" : "",
    row.available ? "" : "is-unavail",
    row.usedThisTurn ? "is-used" : "",
    hovered && !selected && (row.available || drills) ? "is-hover" : "",
    picked > 0 ? "is-picked" : "",
    selected ? "is-selected" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const description = row.blurb ? rulesPlainText(row.blurb) : undefined;
  return (
    <button
      type="button"
      className={cls}
      data-row-id={row.id}
      aria-disabled={row.available || drills ? undefined : true}
      aria-pressed={toggle ? picked > 0 : weaponRow ? selected : undefined}
      aria-description={description || undefined}
      title={rowTitle(row, sub)}
      aria-label={silentReason(row) ? `${row.label} · ${row.reason}` : undefined}
      onClick={onChoose}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={(e) => {
        setKeyFocus(e.currentTarget.matches(":focus-visible"));
        onEnter();
      }}
      onBlur={() => {
        setKeyFocus(false);
        onLeave();
      }}
    >
      <span className="tcm-row-main">
        {row.kind === "mount" || weaponRow ? (
          <>
            {!weaponRow && <span className="tcm-mount-name">{row.secondary}</span>}
            {(row.weapons ?? []).map((w) => (
              <span key={w.key} className="tcm-weapon" data-weapon-key={w.key} onMouseEnter={onWeapon && (() => onWeapon(w.key))} onMouseLeave={onWeapon && (() => onWeapon(undefined))}>
                <span className="tcm-label">{w.name}</span>
                <span className="tcm-stats">
                  {w.range && <span>{w.range}</span>}
                  <span>{w.damage}</span>
                  {w.mode && <span className="tcm-wmode">{w.mode}</span>}
                </span>
                {w.tags.length > 0 && (
                  <span className="tcm-tags">
                    {w.tags.map((t) => (
                      <Term
                        key={t.id}
                        k={`tag:${t.id}`}
                        ctx={t.val !== undefined ? { val: t.val } : undefined}
                        hoverOnly
                        className="tcm-tag"
                        fallback={t.rule !== undefined ? { title: t.label, kind: "Weapon tag", text: t.rule } : undefined}
                      >
                        {t.label}
                      </Term>
                    ))}
                  </span>
                )}
              </span>
            ))}
          </>
        ) : (
          <span className="tcm-label-line">
            <span className="tcm-label">{row.label}</span>
            {/* A charged Recharge item's target (`featureState`); a discharged one says so in its reason line. */}
            {row.recharge?.charged && (
              <span className="tcm-recharge" title={`Recharge ${row.recharge.on}+`}>
                {row.recharge.on}+
              </span>
            )}
            {glyphInline && <span className="tcm-proto" aria-label="protocol available inside" />}
          </span>
        )}
        {sub && <span className={`tcm-sub${sub.help ? " is-help" : ""}${sub.reach !== undefined ? ` is-reach${sub.reach === 0 ? " is-none" : ""}` : ""}`}>{sub.text}</span>}
      </span>
      <span className="tcm-trail">
        {/* The (i) sits at the row's right end, before the cost column: a 12px hit box outside the
            row's natural click area, so a click anywhere else on the row (dead centre included)
            takes the row. A click on the (i) itself is swallowed by the tip. */}
        {!weaponRow && row.kind !== "mount" && <RowInfo row={row} open={keyFocus} shown={sub?.text} />}
        {(weaponRow || row.kind === "mount") && <TagsInfo row={row} open={keyFocus} />}
        <CostSlot row={row} toggle={toggle} have={have} />
        {toggle && (
          <span className={`tcm-check${picked > 0 ? " is-on" : ""}`} aria-label={picked > 1 ? `picked ${picked} times` : undefined}>
            {picked > 1 ? picked : null}
          </span>
        )}
        <span className="tcm-chev" aria-hidden="true">
          {drills ? <Icon icon={faChevronRight} /> : selected ? <Icon icon={faChevronLeft} /> : null}
        </span>
      </span>
    </button>
  );
}

/** The fire card's bottom button label, by progress (the R7 table). */
export const FIRE_EXIT_LABEL: Readonly<Record<FireExit, string>> = {
  cancel: "CANCEL",
  stop_as_skirmish: "STOP FIRING",
  end_early: "END BARRAGE EARLY",
  stop_firing: "STOP FIRING",
};

/** STOP FIRING's note on a Barrage that fired one mount: it converts, refunding one ◆ (the `+◆` in the cost slot). */
export const FIRE_CONVERTS_NOTE = "CONVERTS TO SKIRMISH";

/**
 * A card's bottom button (the fire card's CANCEL / STOP FIRING / END BARRAGE EARLY, END FULL TECH):
 * one line, never wrapping. The label sits left; a note and a refund, when it has them, sit right,
 * the refund in the rows' fixed cost slot so its diamond shares their right edge.
 */
export function FootButton({ label, note, refund = 0, tabIndex, onClick }: { label: string; note?: string; refund?: number; tabIndex?: number | undefined; onClick: () => void }) {
  return (
    <button type="button" className="tcm-stop-btn tcm-one-line" tabIndex={tabIndex} onClick={onClick}>
      <span className="tcm-stop-label">{label}</span>
      {note && <span className="tcm-stop-note">{note}</span>}
      {refund > 0 && (
        <span className="tcm-cost" aria-label={refund === 1 ? "one action back" : `${refund} actions back`}>
          <span className="tcm-cost-plus" aria-hidden="true">
            +
          </span>
          {Array.from({ length: refund }, (_, i) => (
            <span key={i} className="tcm-cost-dot is-refund" />
          ))}
        </span>
      )}
    </button>
  );
}

/** The fire card's bottom button by progress: CANCEL, STOP FIRING (converting a one-mount Barrage, +◆), END BARRAGE EARLY. */
function FireExitButton({ fire, tabIndex, onExit }: { fire: CommandMenuFire; tabIndex?: number | undefined; onExit: () => void }) {
  return <FootButton label={FIRE_EXIT_LABEL[fire.exit]} {...(fire.exit === "stop_as_skirmish" ? { note: FIRE_CONVERTS_NOTE, refund: 1 } : {})} tabIndex={tabIndex} onClick={onExit} />;
}

function ConfirmRow({
  screen,
  pick,
  picks,
  hovered,
  onConfirm,
  onHover,
}: {
  screen: CommandMenuScreen;
  pick: CommandMenuPick;
  picks: string[];
  hovered: boolean;
  onConfirm: () => void;
  onHover: (on: boolean) => void;
}) {
  const noun = pick.noun ?? (screen.id === "barrage" ? "mount" : "option");
  const ready = pickReady(screen, picks);
  const need = pick.count - picks.length;
  const names = picks.map((id) => screen.rows.find((r) => r.id === id)?.label ?? id);
  return (
    <button
      type="button"
      className={`tcm-row tcm-confirm${ready ? "" : " is-unavail"}${hovered && ready ? " is-hover" : ""}`}
      data-row-id={CONFIRM_ID}
      aria-disabled={ready ? undefined : true}
      onClick={onConfirm}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onFocus={() => onHover(true)}
      onBlur={() => onHover(false)}
    >
      <span className="tcm-row-main">
        <span className="tcm-label">{ready ? pick.confirmLabel : `Pick ${need} more ${noun}${need === 1 ? "" : "s"}`}</span>
        <span className="tcm-sub">{names.length > 0 ? names.join(" + ") : "Choose two above"}</span>
      </span>
      <span className="tcm-trail">
        <span className="tcm-cost" aria-label={`costs ${pick.diamonds}`}>
          {Array.from({ length: pick.diamonds }, (_, i) => (
            <span key={i} className="tcm-cost-dot" />
          ))}
        </span>
        {/* The toggles above carry a check; a blank one keeps this cost slot in their column. */}
        <span className="tcm-check is-blank" aria-hidden="true" />
        <span className="tcm-chev" aria-hidden="true">
          <Icon icon={faChevronRight} />
        </span>
      </span>
    </button>
  );
}
